import { supabase } from './supabase';
import { GameState, Item } from '../components/types';

export interface GameStateData {
  mode: string;
  current_level: number;
  total_score: number;
  current_score: number;
  highest_number: number;
  is_level_completed: boolean;
  moves: number;
  tools: Item[];
  language: string;
  diamonds: number;
  sound_on: boolean;
}

/**
 * 游戏状态服务 - 负责与服务器API通信
 */
class GameStateService {
  /**
   * 处理401错误，清除登录态
   */
  private async handleUnauthorized(): Promise<void> {
    try {
      await supabase.auth.signOut();
      localStorage.clear();
      console.log('GameStateService: 401 error, clear login state');
    } catch (error) {
      localStorage.clear();
      console.error('GameStateService: clear login state failed:', error);
    }
  }

  /**
   * 获取游戏状态数据
   * @returns 游戏状态数据
   */
  public async fetchGameState(): Promise<GameStateData | null> {
    try {
      // 获取当前用户会话
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData.session;
      
      if (!session || !session.user) {
        console.error('GameStateService: 用户未登录，无法获取游戏状态');
        return null;
      }
      
      const userId = session.user.id;
      const token = session.access_token;
      
      // 调用API获取游戏状态
      const response = await fetch(`/api/user/game-state?userId=${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // 处理401错误
      if (response.status === 401) {
        await this.handleUnauthorized();
        return null;
      }
      
      if (!response.ok) {
        throw new Error(`获取游戏状态失败: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.gameState) {
        return data.gameState;
      }
      
      return null;
    } catch (error) {
      console.error('GameStateService: 获取游戏状态出错:', error);
      return null;
    }
  }
  
  /**
   * 更新游戏状态数据
   * @param gameState 游戏状态数据
   * @returns 更新后的游戏状态数据
   */
  public async updateGameState(gameState: GameStateData): Promise<GameStateData | null> {
    try {
      // 获取当前用户会话
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData.session;
      
      if (!session || !session.user) {
        console.error('GameStateService: 用户未登录，无法更新游戏状态');
        return null;
      }
      
      const userId = session.user.id;
      const token = session.access_token;
      
      // 调用API更新游戏状态
      const response = await fetch(`/api/user/game-state?userId=${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(gameState)
      });
      
      // 处理401错误
      if (response.status === 401) {
        await this.handleUnauthorized();
        return null;
      }
      
      if (!response.ok) {
        throw new Error(`更新游戏状态失败: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.gameState) {
        return data.gameState;
      }
      
      return null;
    } catch (error) {
      console.error('GameStateService: 更新游戏状态出错:', error);
      return null;
    }
  }
}

// 导出单例实例
export const gameStateService = new GameStateService();
export default gameStateService; 